import React, { useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { CompanyFields, EMPTY_COMPANY, formatApiError } from '../components/CompanyFields';

const ProfilePage = () => {
  const { user } = useAuth();

  if (!user) return <Navigate to="/login" />;

  return (
    <div className="bg-white min-h-screen" data-testid="profile-page">
      <div className="border-b border-gray-200">
        <div className="container-custom px-4 py-6 md:py-8">
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#0a1628]" data-testid="profile-title">Il Mio Profilo</h1>
        </div>
      </div>

      <div className="container-custom px-4 py-6 md:py-10 max-w-2xl mx-auto">
        <div className="bg-white border border-gray-100 rounded-lg p-6 md:p-8">
          {/* Avatar + name */}
          <div className="flex items-center gap-4 mb-8">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#0a1628] text-white flex items-center justify-center text-2xl font-bold">
                {user.name?.charAt(0)}
              </div>
            )}
            <div>
              <h2 className="text-xl font-bold text-[#0a1628]">{user.name}</h2>
              <p className="text-[#64748b] text-sm">{user.email}</p>
              {user.is_verified ? (
                <span className="verified-badge mt-1 inline-block">Verificato</span>
              ) : (
                <span className="text-xs text-red-500 font-bold mt-1 inline-block">Non verificato</span>
              )}
            </div>
          </div>

          {!user.is_verified && (
            <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 mb-6">
              <p className="text-sm">
                <strong>Account non verificato.</strong> Per partecipare alle aste, invia un documento d'identit&agrave; a <a href="mailto:info@equusprime.it" className="text-[#c9a227] font-medium">info@equusprime.it</a>
              </p>
            </div>
          )}

          {/* Read-only data */}
          <div className="space-y-4">
            <InfoRow label="Nome" value={user.name} />
            <InfoRow label="Email" value={user.email} />
            <InfoRow label="Telefono" value={user.phone} />
            <InfoRow label="Tipo account" value={user.account_type === 'azienda' ? 'Azienda' : 'Privato'} />
            {user.account_type === 'azienda' && (
              <>
                <InfoRow label="Ragione sociale" value={user.company_name} />
                <InfoRow label="Partita IVA" value={user.vat_number} />
                <InfoRow label="Sede legale" value={[user.address, user.postal_code, user.city].filter(Boolean).join(', ')} />
              </>
            )}
          </div>

          {user.account_type !== 'azienda' && <SwitchToCompany />}

          <p className="text-xs text-[#64748b] mt-6 pt-4 border-t border-gray-100">
            Per modificare i tuoi dati, contatta l'assistenza a <a href="mailto:info@equusprime.it" className="text-[#c9a227]">info@equusprime.it</a>
          </p>
        </div>
      </div>
    </div>
  );
};

const SwitchToCompany = () => {
  const { login } = useAuth();
  const [open, setOpen] = useState(false);
  const [company, setCompany] = useState(EMPTY_COMPANY);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/auth/company`, company);
      login(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, 'Errore durante il salvataggio'));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="btn-secondary mt-6" data-testid="switch-to-company-btn">
        Sei un'azienda? Passa ad account Azienda
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="mt-6 p-4 bg-[#f8f9fa] space-y-4" data-testid="switch-to-company-form">
      <h3 className="font-display text-lg">Dati aziendali</h3>
      <CompanyFields value={company} onChange={setCompany} />
      {error && <p className="text-red-500 text-sm" data-testid="switch-to-company-error">{error}</p>}
      <div className="flex gap-3">
        <button type="submit" className="btn-primary" disabled={saving} data-testid="switch-to-company-submit">{saving ? 'Salvataggio...' : 'Salva come Azienda'}</button>
        <button type="button" onClick={() => setOpen(false)} className="text-sm text-[#64748b]" data-testid="switch-to-company-cancel">Annulla</button>
      </div>
    </form>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 py-2 border-b border-gray-50" data-testid={`profile-field-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <span className="text-sm text-[#64748b] sm:w-40 flex-shrink-0">{label}</span>
    <span className="text-sm text-[#0a1628] font-medium">{value || '-'}</span>
  </div>
);

export default ProfilePage;
